import { useCallback } from "react";

import { useSalesloftConnectionStatus } from "app/pages/sales-intelligence/modules/salesloft/hooks/useSalesloftConnectionStatus";

import { usePlaybooksSalesloftReconnectHandler } from "./usePlaybooksSalesloftReconnectHandler";
import { usePlaybooksTrackingService } from "./usePlaybooksTrackingService";

type UsePlaybooksSalesloftConnectionParams = {
    enabled?: boolean;
};

/**
 * The playbooks list resolves cadence names through Salesloft, so an expired connection comes back as
 * `cadence: null` — indistinguishable from "no cadence". A lightweight Salesloft probe surfaces the 403,
 * and a successful reconnect refetches the list with real cadences.
 */
export const usePlaybooksSalesloftConnection = ({ enabled = true }: UsePlaybooksSalesloftConnectionParams = {}) => {
    const playbooksTracking = usePlaybooksTrackingService();
    const handleReconnected = usePlaybooksSalesloftReconnectHandler();

    const { isReconnectRequired, openSalesloftConnect } = useSalesloftConnectionStatus({
        enabled,
        onReconnected: handleReconnected,
    });

    const connectSalesloft = useCallback(() => {
        playbooksTracking.trackSalesloftReconnect();
        openSalesloftConnect();
    }, [playbooksTracking, openSalesloftConnect]);

    return { isReconnectRequired: enabled && isReconnectRequired, connectSalesloft };
};
